import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import "./style.scss";
import { Chips } from "primereact/chips";
import { useGetDatasetInfo, useGetDatasetsInfoByTags } from "api/api-hooks/datasets-hooks";
import { TabPanel, TabView } from "primereact/tabview";
import { DatasetCategoryDropdown } from "components/basic/dropdowns/dataset-category/DatasetCategoryDropdown";
import { DatasetInfo as DatasetInfoModel } from "api/model/dataset/DatasetInfo";
import { copyToClipBoard } from "./utils";
import SingleDatasetInfo from "./SingleDatasetInfo";
import DatasetsList from "./DatasetsList";
import DatasetIDSearchField from "./DatasetIDSearchField";

const DatasetInfoSearch = () => {
  const [displayModal, setDisplayModal] = useState(false);
  const [id, setId] = useState<string | null>(null);
  const [idNumber, setIdNumber] = useState<number | undefined>();
  const [selectedType, setSelectedType] = useState<string>("REMOTE_CATEGORIZED_DATASET");
  const typesOfDataset: Array<string> = ["CATEGORIZED_DATASET", "REMOTE_CATEGORIZED_DATASET"];
  const [isCopied, setIsCopied] = useState<boolean>(false);
  const [isSelectedCopied, setIsSelectedCopied] = useState<boolean>(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [defaultCategory, setDefaultCategory] = useState<number>(1);
  const [tags, setTags] = useState<string[]>([]);
  const [selectedDataset, setSelectedDataset] = useState<DatasetInfoModel | null>(null);

  const { data, isLoading, isSuccess, isFetching } = useGetDatasetInfo(id, { enabled: !!id });

  const {
    data: dataByTags,
    isSuccess: isSuccessByTags,
  } = useGetDatasetsInfoByTags(defaultCategory, tags);

  useEffect(() => {
    const fullID: string = `${idNumber?.toString()}:${selectedType}`;
    if (displayModal && idNumber != null) {
      setId(fullID);
    }
  }, [idNumber, selectedType]);

  const onClick = () => {
    setDisplayModal(true);
  };

  const onHide = () => {
    setDisplayModal(false);
    setId(null);
    setIsCopied(false);
    setIsSelectedCopied(false);
    setIdNumber(undefined);
    setSelectedDataset(null);
    setTags([]);
    setDefaultCategory(1);
    setActiveIndex(0);
  };

  const onCopy = () => {
    if (isSuccess && data?.title !== undefined) {
      copyToClipBoard(data.title as string);
      setIsCopied(true);
    }
  };

  const onCopySelected = () => {
    if (selectedDataset && selectedDataset.title !== undefined) {
      copyToClipBoard(selectedDataset.title as string);
      setIsSelectedCopied(true);
    }
  };

  const renderFooter = () => {
    return (
      <div className="mt-4">
        <Button
          label="Close"
          icon="pi pi-times"
          onClick={() => onHide()}
          className="p-button-danger p-button-rounded"
        />
        {activeIndex === 0 ? (
          <Button
            label={isCopied ? "Copied!" : "Copy"}
            icon="pi pi-copy"
            onClick={() => onCopy()}
            autoFocus
            className="p-button-success p-button-rounded"
            disabled={!isSuccess || isLoading || data?.title === null}
          />
        ) : (
          <Button
            label={isSelectedCopied ? "Copied!" : "Copy Selected"}
            icon="pi pi-copy"
            onClick={() => onCopySelected()}
            className="p-button-success p-button-rounded"
            disabled={!selectedDataset || selectedDataset?.title === null}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <Button
        className="p-button-secondary p-button-rounded p-button-text"
        icon="pi pi-search"
        onClick={() => onClick()}
      />
      <Dialog
        header="Dataset information search"
        visible={displayModal}
        style={{ width: activeIndex === 0 ? "40vw" : "50vw" }}
        footer={renderFooter()}
        onHide={() => onHide()}
        blockScroll
      >
        <TabView activeIndex={activeIndex} onTabChange={e => setActiveIndex(e.index)}>
          <TabPanel header="Search by ID">
            <DatasetIDSearchField
              idNumber={idNumber}
              selectedType={selectedType}
              typesOfDataset={typesOfDataset}
              setIdNumber={setIdNumber}
              setSelectedType={setSelectedType}
            />
            {data && (
              <SingleDatasetInfo
                dataToUse={data}
                isLoading={isLoading}
                isSuccess={isSuccess}
                isFetching={isFetching}
                idNumber={idNumber}
              />
            )}
          </TabPanel>
          <TabPanel header="Search by tags">
            <div className="flex flex-row gap-2 mt-4">
              <div className="flex flex-column">
                <label htmlFor="defaultCategory">Select Dataset Category</label>
                <DatasetCategoryDropdown
                  value={defaultCategory}
                  onChange={e => setDefaultCategory(e)}
                  className="mt-2 h-2"
                />
              </div>
              <p className="font-semibold text-2xl mt-5 mb-4 ml-3 mr-3">:</p>
              <div className="flex flex-column">
                <label htmlFor="tags">Tags</label>
                <Chips
                  className="mt-2 h-2"
                  placeholder="Tags"
                  value={tags}
                  onChange={e => setTags(e.value)}
                />
              </div>
            </div>
            <DatasetsList
              isSuccessByTags={isSuccessByTags}
              dataByTags={dataByTags || []}
              selectedDataset={selectedDataset}
              setSelectedDataset={setSelectedDataset}
            />
            {selectedDataset && <SingleDatasetInfo dataToUse={selectedDataset} />}
          </TabPanel>
        </TabView>
      </Dialog>
    </div>
  );
};

export default DatasetInfoSearch;